<template>
	<NotFound v-if="promotionData === null" />
	<div v-if="promotionData">
		<section id="xpromotion-banner">
			<div class="container">
				<h1 class="visually-hidden">{{ promotionData.name }}</h1>
				<div
					v-if="promotionData.content"
					id="editor"
					ref="editor"
					v-html="$filters.htmlAnchorToRouter(promotionData.content)"
					class="editor-content isStyleMobile"
				/>
			</div>
		</section>

		<section v-if="promotionData.stores" id="xpromotion-list">
			<div class="container">
				<PromotionBlock
					v-for="store in promotionData.stores"
					:key="store.id"
					:promotionBlock="store"
					@addPromotion="addPromotion"
				/>
			</div>
		</section>

		<BottomToolbar v-if="editor" :referElement="editor" id="bottomToolbar">
		</BottomToolbar>
	</div>
</template>
<script>
import {
	computed,
	getCurrentInstance,
	onServerPrefetch,
	ref,
	nextTick,
	onMounted,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useHead } from "@vueuse/head";
import PromotionBlock from "@/components/promotion/PromotionBlock.vue";
import BottomToolbar from "@/components/BottomToolbar.vue";
import NotFound from "@/pages/NotFound.vue";
import { useGtm } from "@gtm-support/vue-gtm";
export default {
	name: "Promotion",
	components: {
		PromotionBlock,
		NotFound,
		BottomToolbar,
	},
	setup(props) {
		const globalProperties =
			getCurrentInstance().appContext.config.globalProperties;
		const router = useRouter();
		const route = useRoute();
		const gtm = useGtm();
		const promotionId = router.currentRoute.value.params.promotionId;
		const promotionData = ref();
		const editor = ref();
		const getPromotionData = async () => {
			if (globalProperties.$filters.notNumberString(promotionId)) {
				return (promotionData.value = null);
			}
			const { data } = await globalProperties.axios.get(
				`/api/promotion/${promotionId}`
			);
			if (data.message === "system error") {
				return (promotionData.value = null);
			}
			promotionData.value = data;
			gtm.trackEvent({
				ecommerce: null
			});
			gtm.trackEvent({
				event: "view_promotion",
				ecommerce: {
					promotion_id: `p-${data.id}`,
					promotion_name: data.name,
					items: data.stores.flatMap(store => 
						store.products.map(item => ({
							item_id: item.erp_code,
							item_name: item.erp_name,
							price: parseFloat((item.price_final / item.num).toFixed(2)),
							quantity: item.num,
							promotion_id: `p-${store.id}`,
							promotion_name: store.name,
						}))
					),
				},
				noninteraction: false,
			});
		};

		const promotionHead = computed(() => {
			if (promotionData.value) {
				return {
					meta: [
						{ name: "keywords", content: promotionData.value.name },
						{ name: "description", content: promotionData.value.name },
						{ property: "og:title", content: promotionData.value.name },
						{ property: "og:description", content: promotionData.value.name },
						{ property: "og:image", content: promotionData.value.image_m },
					],
				};
			} else {
				return {};
			}
		});
		useHead(promotionHead);

		if (!promotionData.value) {
			getPromotionData();
		}

		onServerPrefetch(async () => {
			await getPromotionData();
		});

		const store = useStore();
		const addPromotion = async ({ action, product, quantity }) => {
			const { data } = await globalProperties.axios.post(
				"/api/shopping_cart/add",
				{
					product_id: product.id,
					num: quantity,
					cart_id: globalProperties.$storage.getStorageSync("cartId"),
				},
				{ loading: true }
			);
			store.commit("headerStore/setCartNum", data.num);
			if (action === "buy" && !data.max) {
				router.push({ name: "CartStep1" });
			} else {
				globalProperties.$swal.fire({
					title: data.max
						? "該品項已加入至購買上限"
						: data.message == "stock alarm"
						? "庫存不足"
						: `已加入 ${quantity} 件至購物車`,
					icon: data.max
						? "warning"
						: data.message == "stock alarm"
						? "warning"
						: "success",
					timer: 2000,
					didClose: () => {
						if (action === "buy") {
							router.push({ name: "CartStep1" });
						}
					},
				});
			}
			if(!data.max && data.message !== "stock alarm") {
				gtm.trackEvent({ ecommerce: null, pixel: null });
				gtm.trackEvent({
					event: "add_to_cart",
					ecommerce: {
						value: quantity * product.price,
						items:  product.products.map((item) => {
							return {
								item_id: item.erp_code,
								item_name: item.erp_name,
								price: parseFloat((item.price_final / item.num).toFixed(2)),
								quantity: item.num,
								promotion_id: `p-${product.id}`,
								promotion_name: product.name,
							};
						}),
					},
					pixel: {
						content_ids: product.products.map(item => item.erp_code),
						content_type: 'product',
						content_name: product.name,
						contents: product.products.map(item => ({
							id: item.erp_code,
							quantity: item.num,
						})),
					},
					noninteraction: false,
				});
			}
		};

		onMounted(async () => {
			await nextTick();
			const hash = route.hash;
			setTimeout(() => {
				const editorDiv = document.getElementById("editor");
				if (hash) {
					const idHash = editorDiv.querySelector(hash);
					if (idHash) {
						idHash.scrollIntoView({ behavior: "smooth" });
					}
				}
			}, 1000);
		});
		return {
			promotionData,
			addPromotion,
			editor,
		};
	},
};
</script>
<style lang="scss">
#xpromotion-banner {
	margin-bottom: 2rem;
	.banner-body {
		text-align: center;
	}
}

#xpromotion-list {
	.container {
		@media all and (min-width: 768px) {
			display: flex;
			flex-wrap: wrap;
		}
	}
}

#bottomToolbar {
	flex: 1 1 70%;
	display: flex;
	align-items: center;
	justify-content: flex-end;
}
</style>
